import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../config'
import { authFetch } from '../utils/authFetch';
import { Button } from './ui/button';
import { CreditCard, Trash2 } from 'lucide-react';


export default function BindingCards() {
  const [search] = useSearchParams();
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [binding, setBinding] = useState(false);
  const [cards, setCards] = useState([]);

  const [attachFlow, setAttachFlow] = useState({ show: false, requestKey: null, customerKey: null });

  // Notification state (omitted for brevity)
  // Settings state (omitted for brevity)
  useEffect(() => {
    const r = search.get('bindingResult');
    if (r === 'success') toast.success('Карта успешно привязана!');
    if (r === 'failure') toast.error('Не удалось привязать карту');
  }, [search]);

  const fetchPayoutCards = async () => {
    const token = localStorage.getItem('token');
    const res = await authFetch(`${API_URL}/seller/card-payouts-info/details`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.cards) ? data.cards : [];
  };

  const loadCards = async () => {
    setLoadingInfo(true);
    try {
      const list = await fetchPayoutCards();
      setCards(list);
      return list;
    } catch (e) {
      console.error('Ошибка при получении карт:', e);
      setCards([]);
      return [];
    } finally {
      setLoadingInfo(false);
    }
  };

  const pollForPayoutCard = async ({ timeoutMs = 90_000, intervalMs = 2_000 } = {}) => {
    const start = Date.now();
    for (;;) {
      const list = await fetchPayoutCards();
      const active = list.find(c => (c.Status === 'A' || c.Status === 'ACTIVE' || c.Status === 'CONFIRMED') && c.CardId);
      if (active) {
        // зафиксировать выбор на бэкенде (если webhook не используем/не пришёл)
        const token = localStorage.getItem('token');
        await authFetch(`${API_URL}/seller/payout-card/use`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ cardId: active.CardId })
        });

        setCards(list);
        localStorage.removeItem('payoutBindInProgress');
        toast.success('Карта для выплат привязана!');
        return true;
      }
      if (Date.now() - start >= timeoutMs) return false;
      await new Promise(r => setTimeout(r, intervalMs));
    }
  };

  // 1) Первичная загрузка и "фоновый" поллинг, если мы уходим на привязку
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await loadCards();
      const needPolling = localStorage.getItem('payoutBindInProgress') === '1';
      if (!needPolling || cancelled) return;
      setLoadingInfo(true);
      const ok = await pollForPayoutCard({ timeoutMs: 90_000, intervalMs: 2_000 });
      if (!ok && !cancelled) {
        toast.info('Проверили привязку карты. Если карта не появилась — попробуйте ещё раз.');
        setLoadingInfo(false);
        localStorage.removeItem('payoutBindInProgress');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Возврат с 3DS/кабинета банка: реагируем на bindingResult в URL
  useEffect(() => {
    const r = search.get('bindingResult');
    if (!r || r === "failure") return;
    let cancelled = false;
    (async () => {
      setLoadingInfo(true);
      // Быстрый опрос — вдруг вебхук ещё не дошёл
      const ok = await pollForPayoutCard({ timeoutMs: 20_000, intervalMs: 1_000 });
      if (!ok && !cancelled) {
        await loadCards(); // хотя бы свежий список
      }
      localStorage.removeItem('payoutBindInProgress');
      if (!cancelled) setLoadingInfo(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // 3) Если вкладка вернулась в фокус — обновим список (частый кейс на мобильных)
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        loadCards();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleBindingCard() {
    setBinding(true);
    try {
      const jwt = localStorage.getItem('token');
      if (!jwt) return alert('Пожалуйста, войдите');

      // Запустить привязку карты
      const res = await authFetch(`${API_URL}/seller/bind-card-payouts`, {
        method: 'POST',
      });
      if (!res.ok) {
        return alert('Не удалось начать привязку карты');
      }

      const { paymentUrl, requestKey, customerKey } = await res.json();

      if (!paymentUrl) return alert('Не получили paymentUrl из ответа сервера');
      localStorage.setItem('payoutBindInProgress', '1'); // флаг для поллинга после возврата
      window.location.href = paymentUrl;
      return

    } catch (err) {
    alert(err.message);
    console.error(err);
    } finally {
    setBinding(false);
    }
  }

  if (loadingInfo) {
    return <p>Загрузка информации о карте…</p>;
  }

  const deleteCard = async (cardId) => {
    setLoadingInfo(true);
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`${API_URL}/seller/payout-card/${cardId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        return;
      }
      await loadCards();
    } catch (err) {
      console.error('Ошибка при удалении карты:', err);
    } finally {
      setLoadingInfo(false);
    }
  };

  return(
    <div>
      {/* <div className="text-center space-y-2 mb-8">
        <h2 className="gradient-text text-2xl sm:text-3xl font-bold">Безопасность</h2>
        <p className="text-gray-600 text-sm sm:text-base">
          Управляйте картами и настройками безопасности
        </p>
      </div> */}
      {cards.length ? (
        <div className="bg-white rounded-lg border">
            <div className="p-4 sm:p-6 border-b">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                  <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                  Ваши карты
                </h2>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                {cards.map((card) => (
                  <div key={card.CardId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg gap-3">
                    <div className="flex items-center gap-4">
                      <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm sm:text-base">{card.Pan}</p>
                        <p className="text-xs text-gray-500">Действует до {card.ExpDate}</p>
                        <button onClick={() => deleteCard(card.CardId)}>
                        <Trash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
      </div>
      ) : (
        <div className="p-6 flex flex-col items-center bg-white rounded shadow">
        <h2 className="text-xl mb-4">Стать продавцом/покупателем</h2>
        <p className="mb-6 text-gray-600 text-center">
            Чтобы продавать и покупать документы, вам нужно привязать Вашу карту.
        </p>
        <Button
            onClick={handleBindingCard}
            disabled={binding}
            className="bg-gradient-to-r from-[#3466F8] via-[#CF4EE6] to-[#F6275D] text-white"
        >
            {binding ? 'Привязываем…' : 'Привязать карту'}
        </Button>
        </div>
      )
      }
    </div>
  );
  
}